import React, { useState, useEffect } from "react";
import styled from "styled-components";
import NewSpellingGame from "../../components/NewSpellingGame";
import Keyboard from "../../components/Keyboard";
import useSpeech from "../../hooks/useSpeech";
import { Scale } from 'react-scaling';
import Papa from "papaparse";

let filteredQuestions = [];

export default function Game({ ...props }) {
    const [loading, setLoading] = useState(true);
    const [correctQuestions, setCorrectQuestions] = useState([]);
    const [incorrectQuestions, setIncorrectQuestions] = useState([]);
    const [qanswers, setQanswers] = useState([]); 
    const [score, setScore] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentQuestion, setCurrentQuestion] = useState(undefined);
    const { sayWord } = useSpeech();

    const playAudioWithFallback = (question) => {
        if (!question) return;

        const ref = (question.audio_ref || question.word).toLowerCase();
        console.log("Attempting to play from public/audio: " + ref);

        const audioPath = "/audio/" + ref + ".mp3";
        const audio = new Audio(audioPath);

        audio.play()
            .then(() => {
                console.log("Playback started successfully");
            })
            .catch(err => {
                console.warn("Public audio file missing or blocked, falling back to TTS:", err);

                const msg = new SpeechSynthesisUtterance();
                msg.text = question.word;
                window.speechSynthesis.speak(msg);
            });
    };

    useEffect(() => {
        const parseCsv = (csvText) => {
            return new Promise((resolve) => {
                Papa.parse(csvText, {
                    header: false,
                    skipEmptyLines: true,
                    complete: (results) => resolve(results.data.slice(1))
                });
            });
        };

        const fetchCSV = async () => {
            try {
                // Load both CSV files concurrently
                const [spellingRes, morphologyRes] = await Promise.all([
                    fetch("/questions/new_spelling_assessments.csv"),
                    fetch("/questions/new_morphology_assessments.csv")
                ]);

                const spellingText = await spellingRes.text();
                const morphologyText = await morphologyRes.text();

                const [spellingRows, morphologyRows] = await Promise.all([
                    parseCsv(spellingText),
                    parseCsv(morphologyText)
                ]);

                // Combine datasets from both spreadsheets
                const combinedRows = [...spellingRows, ...morphologyRows];

                const rawId = props.assessment_id || "";
                // Remove "newspelling_" or "spelling_" prefix if present
                const cleanId = rawId.replace(/^(newspelling_|spelling_)/i, "");
                const parts = cleanId.split('_');

                const sanitize = (str) => (str || "").toString().replace(/\s/g, "").toLowerCase();

                // Mapping: parts[0] = "morphology", parts[1] = "3", parts[2] = "autumn&spring"
                const targetName = sanitize(parts[0]);
                const targetYear = parts.length > 1 ? sanitize(parts[1]) : "";
                const targetPaper = parts.length > 2 ? sanitize(parts[2]) : "";

                const matches = combinedRows.filter(row => {
                    if (row.length < 6) return false;
                    return sanitize(row[1]) === targetName &&
                        (targetYear === "" || sanitize(row[2]) === targetYear) &&
                        (targetPaper === "" || sanitize(row[3]) === targetPaper);
                }).map(row => {
                    const rawTopicKey = (row[8] || "").toString().trim();
                    return {
                        question_no: parseInt(row[4]) || 0,
                        word: row[5],
                        sentence: row[6],
                        audio_ref: row[7],
                        topic_key: rawTopicKey // Explicitly store string topic key from Column 9
                    };
                }).sort((a, b) => a.question_no - b.question_no);

                console.log(`[Assessment Match] Found ${matches.length} questions for:`, { targetName, targetYear, targetPaper });

                filteredQuestions = matches;
                if (filteredQuestions.length > 0) {
                    setCurrentQuestion(filteredQuestions[0]);
                } else {
                    setCurrentQuestion(null);
                }
                setLoading(false);
            } catch (e) { 
                console.error("Error loading assessment CSVs:", e);
                setLoading(false); 
            }
        };

        fetchCSV();
    }, [props.assessment_id]);

    useEffect(() => {
        if (currentQuestion) {
            playAudioWithFallback(currentQuestion);
        }
    }, [currentQuestion]);

    const [isProcessing, setIsProcessing] = useState(false);

    const handleAnswer = async (value, callback) => {
        if (isProcessing || !currentQuestion) return;
        setIsProcessing(true);

        const userValue = value.trim();
        const isCorrect = currentQuestion.word.toLowerCase() === userValue.toLowerCase();

        const submissionData = {
            ...currentQuestion,
            question: currentQuestion.sentence,
            answer: userValue,
            correct: isCorrect,
            topic_key: currentQuestion.topic_key
        };

        setQanswers(prev => [...prev, submissionData]);

        if (isCorrect) {
            setCorrectQuestions(prev => [...prev, submissionData]);
            setScore(prev => prev + 1);
        } else {
            setIncorrectQuestions(prev => [...prev, submissionData]);
        }

        if (callback) await callback(submissionData);

        const nextIdx = currentIndex + 1;
        if (nextIdx < filteredQuestions.length) {
            setCurrentIndex(nextIdx);
            setCurrentQuestion(filteredQuestions[nextIdx]);
        } else {
            setCurrentIndex(0);
            setCurrentQuestion(null);
        }
        setIsProcessing(false);
    };

    return (
        <NewSpellingGame
            {...props}
            loading={loading}
            correct={correctQuestions}
            incorrect={incorrectQuestions}
            qanswers={qanswers}
            score={score}
            questionLimit={filteredQuestions.length}
            feedback={true}
            canExit={true}
        >
            <Body>
                <Scale scaleTo={1720}>
                    <KeyboardWrapper>
                        {currentQuestion && (
                            <Keyboard
                                currentQuestion={currentQuestion}
                                singleUnderscore={true}
                                onSubmitted={handleAnswer}
                                playAudioButton={true}
                                onPlayAudio={() => playAudioWithFallback(currentQuestion)}
                                additionalCharacters={"abcdefghijklmnopqrstuvwxyz".split("")}
                            />
                        )}
                    </KeyboardWrapper>
                </Scale>
            </Body>
        </NewSpellingGame>
    );
}

const Body = styled.div`
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    padding-top: 180px;
    background: transparent;
`;

const KeyboardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    [class*="controls"], .keyboard-controls, .side-bar {
        order: 2 !important;
        display: flex !important;
        flex-direction: row !important;
        width: 1200px !important;
        justify-content: center !important;
        gap: 50px !important;
        margin-top: 60px !important;
    }

    button, [class*="button-submit"], [class*="button-audio"], .keyboard-button {
        flex: 1 !important;
        min-width: 500px !important; 
        height: 110px !important;
        font-size: 40px !important;
        border-radius: 20px !important;
        font-family: "PrimerPrint";
        box-shadow: 0 6px 0px #204b7b;
        cursor: pointer;
    }

    .keyboard-keys, [class*="keys-container"] {
        order: 1 !important;
    }
`;